import userResources from "api/userResources";
import ConfirmModal from "components/ConfirmModal/ConfirmModal";
import Crossicon from "components/CrossIcon/Crossicon";
import React, { useEffect, useRef, useState } from "react";
import { FaPhone } from "react-icons/fa";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { selectUserInfo } from "../../../redux/userSlice";
import axios from "axios";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import { data } from "autoprefixer";

export default function Banner() {
  const fileInputRef = useRef(null);
  const userInfo = useSelector(selectUserInfo);
  const [progress, setProgress] = useState(0);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [newNumbers, setNewNumbers] = useState("");
  const [disable, setDisable] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [message, setMessage] = useState("");

  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const perPageLimit = 50; // default limit

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCheckboxChange = (number) => {
    setSelectedNumbers((prev) =>
      prev.includes(number)
        ? prev.filter((n) => n !== number)
        : [...prev, number]
    );
  };

  const handleSelectAll = () => {
    const allNumbers = phoneNumbers.map((n) => n.phone_number);
    setSelectedNumbers(
      selectedNumbers.length === allNumbers.length ? [] : allNumbers
    );
  };

  const handleAddNumbers = async () => {
    if (newNumbers) {
      const data = { phone_number: newNumbers };
      const toastId = toast.loading("Adding numbers...");

      try {
        const response = await userResources.AddphoneNumbers(data);

        if (response.status) {
          toast.update(toastId, {
            render: "Numbers added successfully!",
            type: "success",
            isLoading: false,
            autoClose: 3000,
          });
          getNumberData();
          setNewNumbers("");
        }
      } catch (error) {
        toast.update(toastId, {
          render: "Number already exists!",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } else {
      toast.warn("Please enter a number.");
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const Submit = async (e) => {
    e.preventDefault();

    if (!selectedNumbers.length) {
      toast.error("Please select at least one number.");
      return;
    }

    try {
      const data = {
        file: selectedImage,
      };
      const response = await userResources.uploadMedia(data);
      if (response.status === 200) {
        twilioMediaTemplateUpload(response?.data?.data?.filename);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const twilioMediaTemplateUpload = async (fileName) => {
    setDisable(true);
    setProgress(0);

    const total = selectedNumbers.length;
    let successCount = 0;

    const loadingToastId = toast.loading("Sending messages...");

    // ✅ Ensure fileName is a valid URL
    const safeFileUrl = encodeURI(fileName);

    for (const [index, number] of selectedNumbers.entries()) {
      try {
        const payload = {
          phone_numbers: [number],
          template_name: "", // put your Twilio template name
          language_code: "en",
          components: {
            1: message,
            2: safeFileUrl,
          },
        };

        const { status } = await userResources.sendBulkMessagesTemplete(
          payload
        );

        if (status === 200) successCount++;
      } catch (error) {
        console.error(
          `❌ Error sending to ${number}:`,
          error.response?.data || error.message
        );
      }

      // ✅ Progress update
      const percent = Math.round(((index + 1) / total) * 100);
      setProgress(percent);

      toast.update(loadingToastId, {
        render: `Sending messages... ${percent}%`,
        type: "info",
        isLoading: true,
      });

      // ✅ Throttle (5s delay between requests)
      if (index < total - 1) await sleep(5000);
    }

    // ✅ Final toast
    toast.update(loadingToastId, {
      render:
        successCount === total
          ? "🎉 All messages sent successfully!"
          : `✅ ${successCount}/${total} messages sent.`,
      type: successCount === total ? "success" : "warning",
      isLoading: false,
      autoClose: 4000,
    });

    setDisable(false);
    setProgress(0);
  };

  const getNumberData = async (page = 1) => {
    const data = {
      currentPage: page,
      perPageLimit,
    };
    try {
      const response = await userResources.phoneNumbersList(data);

      setPhoneNumbers(response.data.data);
      setPagination(response.data.paginate_data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = async (id) => {
    const loadingToastId = toast.loading("Deleting number...");

    try {
      await userResources.deletePhoneNumbers(id);
      toast.update(loadingToastId, {
        render: "Record deleted successfully.",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      });
      getNumberData();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        "Failed to delete number. Please try again.";
      toast.update(loadingToastId, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      if (toast.isActive(loadingToastId)) toast.dismiss(loadingToastId);
    }
  };

  // Remove duplicates first
  const uniqueNumbers = [
    ...new Map(phoneNumbers.map((n) => [n.phone_number, n])).values(),
  ];

  // Then filter by searchTerm
  const filteredNumbers = uniqueNumbers.filter((n) =>
    n.phone_number.includes(searchTerm)
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      getNumberData(currentPage);
    }, 1000); // 500ms debounce

    return () => {
      clearTimeout(handler); // cleanup on unmount or before next run
    };
  }, [currentPage]);

  return (
    <section>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:grid-cols-6">
        <div className="rounded-md border-2 p-3 sm:col-span-1 md:col-span-2">
          <input
            type="text"
            id="search_number"
            placeholder="Search numbers..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full rounded border border-gray-300 bg-white p-2"
          />
          <div className="my-2 grid grid-cols-2 items-center justify-between">
            <div>
              <input
                type="checkbox"
                checked={
                  selectedNumbers.length === phoneNumbers.length ? true : false
                }
                className="ml-4 mr-2"
                onClick={handleSelectAll}
              />
              <h2
                onClick={handleSelectAll}
                className="inline-block cursor-pointer text-primary"
              >
                {selectedNumbers.length === phoneNumbers.length
                  ? "Unselect All"
                  : "Select All"}
              </h2>
            </div>
            <div className="flex justify-between">
              <div className="flex items-center space-x-3 text-sm font-medium text-gray-700">
                {/* Prev */}
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center justify-center rounded-full border border-gray-300 bg-white 
               text-gray-600 shadow-sm transition hover:bg-gray-100 
               disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <HiChevronLeft size={18} />
                </button>

                {/* Page info */}
                <span className="text-xs text-gray-600">
                  {(currentPage - 1) * perPageLimit + 1}–
                  {Math.min(currentPage * perPageLimit, pagination?.total || 0)}
                  &nbsp;of {pagination?.total}
                </span>

                {/* Next */}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === pagination?.last_page}
                  className="flex items-center justify-center rounded-full border border-gray-300 bg-white
               text-gray-600 shadow-sm transition hover:bg-gray-100 
               disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <HiChevronRight size={18} />
                </button>
              </div>
            </div>
          </div>
          <div className="mb-4 h-96 overflow-scroll rounded border p-4">
            {filteredNumbers.map((n) => (
              <div
                key={n.id}
                className="mb-2 flex items-center justify-between"
              >
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedNumbers.includes(n.phone_number)}
                    onChange={() => handleCheckboxChange(n.phone_number)}
                    className="mr-2"
                  />
                  <p className="dark:text-white">{n.phone_number}</p>
                </div>
                {(userInfo?.data?.role_id ===
                  Number(process.env.REACT_APP_ROLE_SUPERADMIN) ||
                  userInfo?.data?.role_id ===
                    Number(process.env.REACT_APP_ROLE_ADMIN)) && (
                  <ConfirmModal
                    onConfirm={handleDelete}
                    title="Delete Number"
                    message="Are you sure you want to delete this number?"
                    employeeId={n.id}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="mb-4 grid grid-cols-5 items-start">
            <div className="col-span-4 bg-white">
              <div className="relative">
                <PhoneInputWithCountrySelect
                  defaultCountry="CA"
                  value={newNumbers}
                  onChange={(value) => setNewNumbers(value)}
                  placeholder="Enter Phone Number"
                  className="h-10 w-full rounded-md border px-2 text-lg focus:outline-none focus:ring-2 dark:!border-white/10"
                />
                <div className="absolute right-4 top-3">
                  <FaPhone />
                </div>
              </div>
            </div>
            <button
              onClick={handleAddNumbers}
              className="col-span-1 ml-2 rounded bg-green-500 px-4 py-2 text-white"
            >
              Add
            </button>
          </div>
        </div>

        <div className="sm:col-span-2 md:col-span-4">
          <form onSubmit={Submit} className="">
            <div className="flex flex-col gap-6 sm:flex-row">
              {/* Left side - Image Upload */}
              <div className="flex flex-col items-center sm:w-1/2">
                <div
                  className={`relative flex h-64 w-full cursor-pointer items-center justify-center ${
                    imagePreview ? "" : "border-2 border-dashed border-gray-300"
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <>
                      <img
                        src={imagePreview}
                        alt="Selected"
                        className="h-full object-contain"
                      />
                      <button
                        type="button"
                        className="absolute -right-2 -top-2 rounded-full bg-white p-1 shadow"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedImage(null);
                          setImagePreview(null);
                        }}
                      >
                        <Crossicon />
                      </button>
                    </>
                  ) : (
                    <span className="text-4xl text-gray-400">+</span>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  id="imageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
              {/* Right side - Template Variables */}
              <div className="flex-1">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Enter your message"
                  className="mb-2 w-full rounded border border-gray-300 bg-white p-2"
                  rows={10}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className={`mt-4 w-full rounded-lg px-4 py-2 font-medium shadow-md transition 
    ${
      disable
        ? "cursor-not-allowed bg-gray-400 text-gray-200"
        : "bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700"
    }`}
              disabled={disable}
            >
              {disable ? "Submitting..." : "Submit"}
            </button>
          </form>

          {/* Progress Bar */}
          {progress > 0 && (
            <div className="mt-2 w-full rounded bg-gray-200">
              <div
                className="rounded bg-green-500 py-1 text-center text-xs leading-none text-white transition-all"
                style={{ width: `${progress}%` }}
              >
                {progress}%
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
